class RizonProductGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-gallery-track]');
    if (!this.track) return;

    this.slides = Array.from(this.track.querySelectorAll('[data-gallery-slide]'));
    this.thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
    // Each dot button wraps a rizon-carousel-progress span - that inner span
    // carries the class the CSS actually targets, not the button itself.
    this.dots = Array.from(this.querySelectorAll('[data-gallery-dot]')).map((button) => ({
      button,
      progress: button.querySelector('.rizon-carousel-progress'),
    }));

    this.thumbs.forEach((thumb, index) => thumb.addEventListener('click', () => this.goTo(index)));
    this.dots.forEach(({ button }, index) => button.addEventListener('click', () => this.goTo(index)));
    this.track.addEventListener('scroll', this.onScroll.bind(this), { passive: true });

    // The color swatches live in a sibling section element (rizon-main-product),
    // so the two talk through a document-level event rather than reaching into
    // each other's DOM.
    this.variantImage = this.querySelector('[data-gallery-variant-image]');
    this.variantThumb = this.querySelector('[data-gallery-variant-thumb]');
    if (this.variantImage) {
      this.onVariantImageChange = this.setVariantImage.bind(this);
      document.addEventListener('rizon:variant:image', this.onVariantImageChange);
    }

    this.updateActive();
  }

  disconnectedCallback() {
    if (this.onVariantImageChange) {
      document.removeEventListener('rizon:variant:image', this.onVariantImageChange);
    }
  }

  // Swap the lead slide's photo to the newly selected color. The incoming
  // file is decoded BEFORE anything visible changes, so the fade-out /
  // fade-in never exposes a half-loaded or empty slide.
  setVariantImage(event) {
    const { src, srcset, alt } = event.detail || {};
    if (!src || !this.variantImage) return;
    if (this.variantImage.getAttribute('src') === src) return;

    // Scroll back to the variant slide so the change is actually on screen
    // even if the shopper had swiped to a different photo.
    this.goTo(0);

    const apply = () => {
      this.variantImage.src = src;
      if (srcset) this.variantImage.srcset = srcset;
      if (alt !== undefined) this.variantImage.alt = alt;
      if (this.variantThumb) {
        this.variantThumb.src = src;
        if (alt !== undefined) this.variantThumb.alt = alt;
      }
    };

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      apply();
      return;
    }

    const preload = new Image();
    if (srcset) preload.srcset = srcset;
    preload.src = src;

    const swap = () => {
      this.variantImage.classList.add('is-swapping');
      this.variantThumb?.classList.add('is-swapping');
      window.clearTimeout(this.swapTimer);
      this.swapTimer = window.setTimeout(() => {
        apply();
        this.variantImage.classList.remove('is-swapping');
        this.variantThumb?.classList.remove('is-swapping');
      }, 180);
    };

    if (preload.decode) {
      preload.decode().then(swap).catch(swap);
    } else {
      preload.onload = swap;
      preload.onerror = swap;
    }
  }

  goTo(index) {
    const slide = this.slides[index];
    if (!slide) return;
    this.track.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
  }

  onScroll() {
    window.clearTimeout(this.scrollEndTimer);
    this.scrollEndTimer = window.setTimeout(() => this.updateActive(), 100);
  }

  updateActive() {
    if (!this.slides.length) return;
    const trackRect = this.track.getBoundingClientRect();
    let closestIndex = 0;
    let closestDistance = Infinity;
    this.slides.forEach((slide, index) => {
      const distance = Math.abs(slide.getBoundingClientRect().left - trackRect.left);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });
    this.thumbs.forEach((thumb, index) => thumb.classList.toggle('is-active', index === closestIndex));
    this.dots.forEach(({ progress }, index) => {
      if (progress) progress.classList.toggle('rizon-carousel-progress--active', index === closestIndex);
    });
  }
}

if (!customElements.get('rizon-product-gallery')) {
  customElements.define('rizon-product-gallery', RizonProductGallery);
}
